import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface StudyStreakData {
  heatmapData: Array<{ date: string; minutes: number }>;
  currentStreak: number;
  longestStreak: number;
  totalDays: number;
}

/**
 * 学習ストリーク（連続学習日数）とヒートマップデータを取得
 */
export function useStudyStreak(userId: string | undefined) {
  const [data, setData] = useState<StudyStreakData>({
    heatmapData: [],
    currentStreak: 0,
    longestStreak: 0,
    totalDays: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchStreakData = async () => {
      try {
        // 過去1年分のセッションデータを取得
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        const { data: sessions, error } = await supabase
          .from('study_sessions')
          .select('session_date, duration_minutes')
          .eq('user_id', userId)
          .gte('session_date', oneYearAgo.toISOString().split('T')[0])
          .order('session_date', { ascending: true });

        if (error) throw error;

        // 日付ごとの学習時間を集計
        const dailyMinutes: Record<string, number> = {};
        sessions?.forEach((session) => {
          const date = session.session_date;
          dailyMinutes[date] = (dailyMinutes[date] || 0) + session.duration_minutes;
        });

        // ヒートマップ用データを生成（過去84日分）
        const heatmapData: Array<{ date: string; minutes: number }> = [];
        for (let i = 83; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          heatmapData.push({
            date: dateStr,
            minutes: dailyMinutes[dateStr] || 0,
          });
        }

        // 現在のストリークを計算
        let currentStreak = 0;
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        // 今日または昨日から遡ってカウント
        const startFrom = dailyMinutes[today] ? today : yesterdayStr;
        if (dailyMinutes[startFrom]) {
          let checkDate = new Date(startFrom);
          while (true) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dailyMinutes[dateStr] && dailyMinutes[dateStr] > 0) {
              currentStreak++;
              checkDate.setDate(checkDate.getDate() - 1);
            } else {
              break;
            }
          }
        }

        // 最長ストリークを計算
        let longestStreak = 0;
        let tempStreak = 0;
        const allDates = Object.keys(dailyMinutes).sort();
        
        for (let i = 0; i < allDates.length; i++) {
          if (dailyMinutes[allDates[i]] > 0) {
            tempStreak++;
            if (i > 0) {
              const prevDate = new Date(allDates[i - 1]);
              const currDate = new Date(allDates[i]);
              const dayDiff = Math.floor(
                (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24)
              );
              if (dayDiff > 1) {
                tempStreak = 1;
              }
            }
            longestStreak = Math.max(longestStreak, tempStreak);
          } else {
            tempStreak = 0;
          }
        }

        // 総学習日数
        const totalDays = Object.values(dailyMinutes).filter((m) => m > 0).length;

        setData({
          heatmapData,
          currentStreak,
          longestStreak,
          totalDays,
        });
      } catch (error) {
        console.error('学習ストリークデータの取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStreakData();
  }, [userId]);

  return { ...data, loading };
}
